import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import api from "@/lib/api";

const TYPES = [
    { value: "commercial_contract", label: "Commercial Contract" },
    { value: "litigation",         label: "Litigation" },
    { value: "employment",         label: "Employment" },
    { value: "ip",                 label: "Intellectual Property" },
    { value: "corporate_governance", label: "Corporate Governance" },
    { value: "compliance",         label: "Compliance" },
    { value: "claims",             label: "Claims" },
    { value: "gov_request",        label: "Government Request" },
    { value: "other",              label: "Other" },
];

const PRIVILEGE = [
    { value: "attorney_client", label: "Attorney-Client" },
    { value: "work_product",    label: "Work Product" },
    { value: "not_privileged",  label: "Not Privileged" },
];

export default function LegalNewMatter() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        title: "",
        description: "",
        matter_type: "commercial_contract",
        jurisdiction: "",
        court: "",
        opposing_counsel: "",
        privilege_default: "attorney_client",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/legal/matters", {
                ...form,
                title: form.title.trim(),
                description: form.description.trim() || null,
                jurisdiction: form.jurisdiction.trim() || null,
                court: form.court.trim() || null,
                opposing_counsel: form.opposing_counsel.trim() || null,
                parties: [],
                attorneys_of_record: [],
                ai_team: [],
                ethical_wall_users: [],
            });
            toast.success(`Matter ${data.matter_number} opened.`);
            nav(`/app/legal/matters/${data.id}`);
        } catch (e) {
            const msg = e.response?.data?.detail?.message || e.response?.data?.detail || "Failed to create matter.";
            toast.error(typeof msg === "string" ? msg : "Failed to create matter.");
        } finally {
            setSaving(false);
        }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar" data-testid="new-matter-page">
            <div className="max-w-2xl mx-auto p-8">
                <Link
                    to="/app/legal/matters"
                    className="inline-flex items-center gap-1 text-[13px] text-gray-500 hover:text-gray-900"
                    data-testid="new-matter-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to matters
                </Link>

                <h1 className="mt-3 text-2xl font-medium tracking-tight text-gray-900">Open a new matter</h1>
                <p className="mt-1 text-sm text-gray-500">
                    A matter number will be assigned automatically. You can update parties, ethical walls, and the AI team after creation.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="new-matter-form">
                    <div>
                        <label className="block text-[12px] font-medium text-gray-700 mb-1">Title *</label>
                        <input
                            required
                            value={form.title}
                            onChange={set("title")}
                            data-testid="matter-title"
                            placeholder="e.g., Acme Corp v Widget Co — patent infringement"
                            className="w-full rounded-lg border border-[#e8e8e3] bg-white px-3 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                        />
                    </div>

                    <div>
                        <label className="block text-[12px] font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={form.description}
                            onChange={set("description")}
                            rows={3}
                            data-testid="matter-description"
                            placeholder="Optional — a short summary of the dispute or engagement."
                            className="w-full rounded-lg border border-[#e8e8e3] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a] resize-y"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[12px] font-medium text-gray-700 mb-1">Matter type *</label>
                            <select
                                required
                                value={form.matter_type}
                                onChange={set("matter_type")}
                                data-testid="matter-type"
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white px-3 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            >
                                {TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[12px] font-medium text-gray-700 mb-1">Privilege default</label>
                            <select
                                value={form.privilege_default}
                                onChange={set("privilege_default")}
                                data-testid="matter-privilege"
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white px-3 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            >
                                {PRIVILEGE.map((p) => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[12px] font-medium text-gray-700 mb-1">Jurisdiction</label>
                            <input
                                value={form.jurisdiction}
                                onChange={set("jurisdiction")}
                                data-testid="matter-jurisdiction"
                                placeholder="e.g., US-NY, EU-DE"
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white px-3 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            />
                        </div>
                        <div>
                            <label className="block text-[12px] font-medium text-gray-700 mb-1">Court</label>
                            <input
                                value={form.court}
                                onChange={set("court")}
                                data-testid="matter-court"
                                placeholder="e.g., SDNY"
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white px-3 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[12px] font-medium text-gray-700 mb-1">Opposing counsel</label>
                        <input
                            value={form.opposing_counsel}
                            onChange={set("opposing_counsel")}
                            data-testid="matter-opposing-counsel"
                            placeholder="Optional"
                            className="w-full rounded-lg border border-[#e8e8e3] bg-white px-3 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                        />
                    </div>

                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
                        <b>Attorney supervision reminder.</b> Everything drafted here starts as{" "}
                        <code className="text-[11.5px]">DRAFT — PENDING ATTORNEY REVIEW</code>. An
                        attorney of record or a user with the <code>attorney</code> role must approve
                        before any document can be filed or served.
                    </div>

                    <div className="pt-2 flex items-center justify-end gap-3">
                        <Link
                            to="/app/legal/matters"
                            className="text-sm text-gray-600 hover:text-gray-900"
                            data-testid="new-matter-cancel"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="submit-new-matter"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-10 text-sm font-medium text-white hover:bg-[#15803d] disabled:opacity-60"
                        >
                            <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Opening…" : "Open matter"}
                        </button>
                    </div>
                </form>
                <div className="h-16" />
            </div>
        </div>
    );
}
